<script setup>
import {
    convertPriorityToInjurySeverity,
    HpoBodyStatus,
    HpoBodyStatusLabel,
    HpoRole,
    InjurySeverityLabel
} from "../../choices";
import {priorityColor} from "../../utils";
import {GenderLabel, PoliceVerificationLabels, YesNo, YesNoLabels} from "@/choices";
import {patientInHospital, patientTransported} from "./td/utils";
import {isMobile} from "@/utils/breakpoints";
import {displayValue} from "@/utils/utils";
import {
    mdiAmbulance,
    mdiCheckBold,
    mdiGestureTap,
    mdiHospitalBuilding,
    mdiMapMarker,
    mdiTimerSandFull,
    mdiTruckPlusOutline
} from "@mdi/js";
import {computed} from "vue";
import PatientImage from "@/components/hpo/dashboard/patientListItems/PatientImage.vue";

const props = defineProps({
    patient: {
        type: Object,
        required: true
    },
    countryName: {
        type: String,
        required: true
    },
    member: {
        type: Object,
        required: true
    },
    readOnly: {
        type: Boolean,
        default: false
    },
})

const emit = defineEmits(['displayActionsModal'])


const priority = computed(() => {
    return props.patient.transportPriority ?? props.patient.treatmentPriority
})

const birthDate = computed(() => {
    if (!props.patient.birthYear && !props.patient.birthMonth && !props.patient.birthDay) {
        return displayValue(null)
    }
    return `${displayValue(props.patient.birthDay)}/${displayValue(props.patient.birthMonth)}/${displayValue(props.patient.birthYear)}`
})

function patientStatus(patient) {
    if (patientInHospital(patient)) {
        return pgettext('hpo_patient_status', 'in hospital')
    } else if (patientTransported(patient)) {
        return pgettext('hpo_patient_status', 'transport')
    } else {
        return pgettext('hpo_patient_status', 'on site')
    }
}

function getYesNo(value) {
    if (value === true) {
        return YesNoLabels[YesNo.YES]
    } else {
        return YesNoLabels[YesNo.NO]
    }
}

function hospitalName(patient) {
    try {
        return patient.transport.lastDestinationRequest.hospital.name
    } catch (e) {
        return null
    }
}

function displayActionsModal() {
    emit('displayActionsModal', props.patient)
}

const patientStatusIcon = {
    [HpoBodyStatus.AT_SCENE]: [mdiMapMarker, 'red'],
    [HpoBodyStatus.IN_TRANSPORT]: [mdiAmbulance, 'blue-darken-2'],
    [HpoBodyStatus.IN_HOSPITAL]: [mdiHospitalBuilding, 'cyan-darken-1'],
    [HpoBodyStatus.RELEASED]: [mdiCheckBold, 'green'],
    [HpoBodyStatus.TRANSPORT_PENDING]: [mdiTimerSandFull, 'orange'],
    [HpoBodyStatus.TRANSPORT_READY]: [mdiTruckPlusOutline, 'purple-darken-1'],
};
</script>

<template>
    <v-card border="sm" class="hpo-izs-patient-strip mb-4"
            :class="`hpo-izs-patient-strip--priority-${priorityColor(priority)}`">
        <div class="hpo-izs-patient-strip__header">
            <h3>
                <span class="hpo-izs-patient-strip__tic-number">
                    {{ patient.tic }}
                </span>
                {{ patient.lastName }} {{ patient.firstName }}
            </h3>
            <v-btn v-if="!readOnly && member.role !== HpoRole.KOPIS"
                   density="compact"
                   :prepend-icon="mdiGestureTap"
                   :text="pgettext('action', 'Options')"
                   @click="displayActionsModal"
            ></v-btn>
        </div>

        <div v-if="isMobile" class="hpo-izs-patient-strip__body">
            <dl>
                <dt>{{ pgettext('label', 'Injury') }}:</dt>
                <dd class="hpo-priority">
                    <v-chip :color="priorityColor(priority) || 'grey-lighten-3'" variant="flat"
                            density="compact" class="h-auto px-6">
                        <strong>
                            {{ InjurySeverityLabel[convertPriorityToInjurySeverity(priority)].toUpperCase() }}
                        </strong>
                    </v-chip>
                </dd>
                <dt>{{ pgettext('label', 'Patient status') }}:</dt>
                <dd>
                    <v-chip color="grey-lighten-3" variant="flat" density="compact" class="h-auto pl-2">
                        <v-icon v-if="patientStatusIcon[patient.status]" :icon="patientStatusIcon[patient.status][0]"
                                :color="patientStatusIcon[patient.status][1]" class="mr-1"/>
                        {{ HpoBodyStatusLabel[patient.status] }}
                    </v-chip>
                </dd>
                <dt>{{ pgettext('label', 'Gender') }}:</dt>
                <dd>
                    <span>{{ displayValue(GenderLabel[patient.gender]) }}</span>
                    <span v-if="patient.isChild">
                        {{ ' (' }}{{ gettext('child').toUpperCase() }}{{ ')' }}
                    </span>
                </dd>
                <dt>{{ pgettext('label', 'Birth Date') }}:</dt>
                <dd>
                    {{ patient.birthDate ? formatShortDate(patient.birthDate) : birthDate }}
                </dd>
                <dt>{{ pgettext('label', 'Police Verification') }}:</dt>
                <dd>{{ getYesNo(patient.policeVerification) }}</dd>
            </dl>
        </div>


        <div v-else class="hpo-izs-patient-strip__body">
            <dl>
                <dt>{{ pgettext('label', 'Injury') }}:</dt>
                <dd class="hpo-priority">
                    <v-chip :color="priorityColor(priority) || 'grey-lighten-3'" variant="flat"
                            density="compact" class="h-auto px-6">
                        <strong>
                            {{ InjurySeverityLabel[convertPriorityToInjurySeverity(priority)].toUpperCase() }}
                        </strong>
                    </v-chip>
                </dd>
                <dt>{{ pgettext('label', 'TIC Card Number') }}:</dt>
                <dd>{{ displayValue(patient.tic) }}</dd>
                <dt>{{ pgettext('label', 'Gender') }}:</dt>
                <dd>
                    <span>{{ GenderLabel[patient.gender] }}</span>
                    <span v-if="patient.isChild">
                            {{ ' (' }}{{ gettext('child').toUpperCase() }}{{ ')' }}
                        </span>
                </dd>
                <dt>{{ pgettext('label', 'Last Name') }}:</dt>
                <dd>{{ displayValue(patient.lastName) }}</dd>
                <dt>{{ pgettext('label', 'First Name') }}:</dt>
                <dd>{{ displayValue(patient.firstName) }}</dd>

                <dt>{{ pgettext('label', 'Birth Date') }}:</dt>
                <dd>
                    {{ patient.birthDate ? formatShortDate(patient.birthDate) : birthDate }}
                </dd>
                <dt>{{ pgettext('label', 'Birth number') }}:</dt>
                <dd>{{ displayValue(patient.birthNumber) }}</dd>
                <dt>{{ pgettext('label', 'Insurance Company') }}:</dt>
                <dd>{{ displayValue(patient.insurance) }}</dd>
            </dl>
            <dl>
                <dt>{{ pgettext('label', 'Patient status') }}:</dt>
                <dd>
                    <v-chip color="grey-lighten-3" variant="flat" density="compact" class="h-auto pl-2">
                        <v-icon v-if="patientStatusIcon[patient.status]" :icon="patientStatusIcon[patient.status][0]"
                                :color="patientStatusIcon[patient.status][1]" class="mr-1"/>
                        {{ HpoBodyStatusLabel[patient.status] }}
                    </v-chip>
                </dd>
                <dt>{{ pgettext('label', 'Destination hospital') }}:</dt>
                <dd>
                    <template v-if="patient.hospital">
                        {{ displayValue(patient.hospital.name) }}
                    </template>
                    <template v-else>
                        {{ displayValue(null) }}
                    </template>
                </dd>
                <!-- TODO: Segregate to separate dashboard for hospital info-lines where only hospital's patients would be listed -->
                <dt>{{ pgettext('label', 'Destination Ward') }}:</dt>
                <dd>
                    <template v-if="patient.destinationWard">
                        {{ displayValue(patient.destinationWard) }}
                    </template>
                    <template v-else>
                        {{ displayValue(null) }}
                    </template>
                </dd>
                <dt>{{ pgettext('label', 'Country') }}:</dt>
                <dd>{{ countryName }}</dd>
                <dt>{{ pgettext('label', 'Address') }}:</dt>
                <dd>{{ displayValue(patient.address) }}</dd>
                <dt>{{ pgettext('label', 'City') }}:</dt>
                <dd>{{ displayValue(patient.city) }}</dd>
                <dt>{{ pgettext('label', 'Phone number') }}:</dt>
                <dd>{{ displayValue(patient.phoneNumber) }}</dd>
                <dt>{{ pgettext('label', 'Police Verification') }}:</dt>
                <dd>{{ PoliceVerificationLabels[patient.policeVerification] }}</dd>
            </dl>
            <dl>
                <dt>{{ pgettext('label', 'Note') }}:</dt>
                <dd>{{ displayValue(patient.note) }}</dd>
            </dl>
        </div>

        <div v-if="patient.images && patient.images.length > 0" class="pa-2 pt-0">
            <div class="d-flex flex-wrap ga-3">
                <PatientImage v-for="image in patient.images" :key="image.id" :image="image" small/>
            </div>
        </div>
    </v-card>
</template>